import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  Typography,
  Avatar,
  Button,
  TextField,
  IconButton,
  CircularProgress,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  styled,
  alpha,
  InputAdornment,
} from '@mui/material';
import {
  Business as BusinessIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  PhotoCamera as PhotoCameraIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Language as LanguageIcon,
  Badge as BadgeIcon,
  People as PeopleIcon,
  CheckCircle as CheckCircleIcon,
  Person as PersonIcon,
  Work as WorkIcon,
  Category as CategoryIcon,
} from '@mui/icons-material';
import { doc, getDoc, setDoc, updateDoc, collection, query, where, getDocs, Timestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';
import { Profile, COLLECTIONS } from '../../types/firebase';
import { useTheme } from '../../contexts/ThemeContext';
import ImageCropModal from '../ImageCropModal';

// Styled components - use theme glass; only add layout and subtle overlay
const ProfileCard = styled(Card)(({ theme }) => ({
  overflow: 'visible',
  position: 'relative',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  ...(theme.palette.mode === 'dark' && {}),
  '&::after': {
    content: '""',
    position: 'absolute',
    top: '1px',
    left: '1px',
    right: '1px',
    height: '50%',
    borderRadius: '23px 23px 0 0',
    background: theme.palette.mode === 'light'
      ? 'linear-gradient(180deg, rgba(255, 255, 255, 0.3) 0%, rgba(255, 255, 255, 0) 100%)'
      : 'linear-gradient(180deg, rgba(255, 255, 255, 0.06) 0%, rgba(255, 255, 255, 0) 100%)',
    pointerEvents: 'none',
  },
}));

const HeaderSection = styled(Box)(({ theme }) => ({
  ...(theme.palette.mode === 'light'
    ? {
        background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.85) 0%, rgba(26, 26, 26, 0.9) 100%)',
        backdropFilter: 'blur(15px) saturate(150%)',
        WebkitBackdropFilter: 'blur(15px) saturate(150%)',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          borderRadius: '24px 24px 0 0',
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
          opacity: 0.15,
          pointerEvents: 'none',
        },
      }
    : {
        backgroundColor: 'rgba(255, 255, 255, 0.06)',
        backdropFilter: 'blur(40px)',
        WebkitBackdropFilter: 'blur(40px)',
        border: 'none',
        borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
        boxShadow: 'inset 0 1px 0 0 rgba(255,255,255,0.06)',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          borderRadius: '24px 24px 0 0',
          background: 'transparent',
          pointerEvents: 'none',
        },
      }),
  padding: '30px',
  position: 'relative',
  borderRadius: '24px 24px 0 0',
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  display: 'inline-block',
  marginBottom: '20px',
}));

const CompanyLogo = styled(Avatar)(({ theme }) => ({
  width: 120,
  height: 120,
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  fontSize: '3rem',
  fontWeight: 'bold',
  border: `4px solid ${theme.palette.common.white}`,
  boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.3)}`,
  '& img': {
    objectFit: 'cover',
    width: '100%',
    height: '100%',
  },
  '& .MuiAvatar-img': {
    objectFit: 'cover',
    width: '100%',
    height: '100%',
  },
}));

const UploadButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  bottom: 0,
  right: 0,
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.primary.main,
  border: `2px solid ${theme.palette.common.white}`,
  '&:hover': {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
  },
}));

const InfoSection = styled(Box)(({ theme }) => ({
  padding: '30px',
}));

const InfoGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
  gap: '20px',
  marginTop: '20px',
}));

const InfoItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '12px',
  borderRadius: '10px',
  backgroundColor: alpha(theme.palette.primary.main, 0.05),
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
    transform: 'translateY(-2px)',
    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`,
  },
}));

const EditButton = styled(Button)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
  color: theme.palette.common.white,
  borderRadius: '10px',
  padding: '10px 24px',
  fontWeight: 'bold',
  textTransform: 'none',
  boxShadow: `0 4px 15px ${alpha(theme.palette.primary.main, 0.3)}`,
  '&:hover': {
    background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.primary.main} 100%)`,
    boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`,
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: theme.palette.background.paper,
    '&:hover fieldset': {
      borderColor: theme.palette.primary.main,
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main,
    },
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: theme.palette.primary.main,
  },
}));

interface CompanyProfileProps {
  onUpdate?: () => void;
  /** When true, render only the elements (logo, name, users, status) without the card wrapper—e.g. for dashboard right column */
  compact?: boolean;
}

interface LogoWithFallbackProps {
  src: string;
  alt: string;
  companyName: string;
  onError?: () => void;
  size?: number;
}

const LogoWithFallback: React.FC<LogoWithFallbackProps> = ({ src, alt, companyName, onError, size }) => {
  const [imageError, setImageError] = useState(false);

  const handleImageError = () => {
    console.error('Logo image failed to load:', src);
    setImageError(true);
    if (onError) onError();
  };

  const sizeSx = size ? { width: size, height: size } : undefined;

  if (imageError) {
    return (
      <CompanyLogo sx={sizeSx}>
        <BusinessIcon sx={{ fontSize: size && size <= 88 ? '2rem' : '3rem' }} />
      </CompanyLogo>
    );
  }

  return (
    <CompanyLogo
      src={src}
      alt={alt}
      onError={handleImageError}
      onLoad={() => console.log('Logo loaded successfully:', src)}
      sx={sizeSx}
    />
  );
};

const CompanyProfile: React.FC<CompanyProfileProps> = ({ onUpdate, compact = false }) => {
  const { userProfile } = useAuth();
  const { colors } = useTheme();
  const [companyData, setCompanyData] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [editData, setEditData] = useState<any>({});
  const [userCount, setUserCount] = useState(0);
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (userProfile?.masterOwnerID) {
      fetchCompanyProfile();
      fetchUserCount();
    }
  }, [userProfile]);

  // Listen for edit profile event from Settings tab
  useEffect(() => {
    const handleEditProfileEvent = () => {
      setEditMode(true);
    };

    window.addEventListener('editCompanyProfile', handleEditProfileEvent);
    return () => {
      window.removeEventListener('editCompanyProfile', handleEditProfileEvent);
    };
  }, []);

  const fetchCompanyProfile = async () => {
    if (!userProfile?.masterOwnerID) return;

    setLoading(true);
    try {
      console.log('Fetching company profile for masterOwnerID:', userProfile.masterOwnerID);

      // First try to get from companies collection
      const profileRef = doc(db, COLLECTIONS.COMPANIES, userProfile.masterOwnerID);
      const profileSnap = await getDoc(profileRef);

      if (profileSnap.exists()) {
        const data = { ...profileSnap.data(), id: profileSnap.id } as Profile;
        console.log('=== LOGO RETRIEVAL DEBUG ===');
        console.log('Company profile data:', data);
        console.log('Logo URL from database (logo field):', data.logo);
        console.log('Logo URL from database (logoUrl field):', (data as any).logoUrl);
        console.log('Logo URL type:', typeof data.logo);
        console.log('Logo URL length:', data.logo ? data.logo.length : 0);
        console.log('============================');
        
        // Handle both logo and logoUrl fields for compatibility
        if (!data.logo && (data as any).logoUrl) {
          data.logo = (data as any).logoUrl;
          console.log('Using logoUrl field as logo:', data.logo);
        }

        setCompanyData(data);
        setEditData({
          companyName: data.companyName || '',
          industry: data.industry || '',
          department: data.department || '',
          firstName: data.firstName || '',
          lastName: data.lastName || '',
          title: data.title || '',
          email: data.email || userProfile.email || '',
          phone: (data as any).phoneNumber || data.phone || '',
          address: (data as any).streetAddress1 || data.address || '',
          website: data.website || '',
        });
      } else {
        console.log('No profile found, creating new profile');
        // Create a new profile if it doesn't exist
        const newProfile: Partial<Profile> = {
          companyName: 'Your Company',
          department: '',
          email: userProfile.email || '',
          masterOwnerID: userProfile.masterOwnerID,
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
        };

        await setDoc(profileRef, newProfile);
        setCompanyData({ ...newProfile, id: userProfile.masterOwnerID } as Profile);
        setEditData({
          companyName: newProfile.companyName || '',
          industry: '',
          department: newProfile.department || '',
          firstName: '',
          lastName: '',
          title: '',
          email: newProfile.email || '',
          phone: '',
          address: '',
          website: '',
        });
      }
    } catch (err) {
      console.error('Error fetching company profile:', err);
      setError('Failed to load company profile');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserCount = async () => {
    if (!userProfile?.masterOwnerID) return;

    try {
      const usersQuery = query(
        collection(db, COLLECTIONS.USERS),
        where('masterOwnerID', '==', userProfile.masterOwnerID)
      );
      const snapshot = await getDocs(usersQuery);
      setUserCount(snapshot.size);
    } catch (err) {
      console.error('Error fetching user count:', err);
    }
  };

  const handleLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !companyData) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image size should be less than 5MB');
      return;
    }

    // Open crop modal instead of directly uploading
    setSelectedFile(file);
    setCropModalOpen(true);

    // Clear the input so the same file can be selected again
    if (event.target) {
      event.target.value = '';
    }
  };

  const handleCroppedImageUpload = async (croppedImageBlob: Blob) => {
    if (!companyData || !userProfile?.masterOwnerID) return;

    setUploading(true);
    setError(null);

    try {
      // Delete old logo if exists
      if (companyData.logo) {
        try {
          const oldLogoRef = ref(storage, companyData.logo);
          await deleteObject(oldLogoRef);
        } catch (err) {
          console.log('Old logo not found or already deleted');
        }
      }

      // Upload cropped image
      const timestamp = Date.now();
      const fileName = `company-logos/${userProfile.masterOwnerID}/${timestamp}-cropped-logo.jpg`;
      const storageRef = ref(storage, fileName);
      const snapshot = await uploadBytes(storageRef, croppedImageBlob);
      const downloadURL = await getDownloadURL(snapshot.ref);

      // Update profile with new logo URL
      const profileRef = doc(db, COLLECTIONS.COMPANIES, companyData.id);
      await updateDoc(profileRef, {
        logo: downloadURL,
        updatedAt: Timestamp.now(),
      });

      console.log('=== LOGO UPLOAD DEBUG ===');
      console.log('Uploaded logo URL:', downloadURL);
      console.log('Storage path:', fileName);
      console.log('Company ID:', companyData.id);
      console.log('========================');

      setCompanyData({ ...companyData, logo: downloadURL });
      setSuccess('Logo uploaded and edited successfully');
    } catch (err) {
      console.error('Error uploading cropped logo:', err);
      setError('Failed to upload logo');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!companyData) return;

    setSaving(true);
    try {
      const profileRef = doc(db, COLLECTIONS.COMPANIES, companyData.id);
      
      // Map form fields to Firebase field names
      const updateData = {
        companyName: editData.companyName,
        industry: editData.industry,
        department: editData.department,
        firstName: editData.firstName,
        lastName: editData.lastName,
        title: editData.title,
        email: editData.email,
        phoneNumber: editData.phone, // Map phone to phoneNumber
        streetAddress1: editData.address, // Map address to streetAddress1
        website: editData.website,
        updatedAt: Timestamp.now(),
      };
      
      await updateDoc(profileRef, updateData);

      // Update local state with the mapped data
      setCompanyData({ 
        ...companyData, 
        ...updateData,
        phone: editData.phone, // Keep both for compatibility
        address: editData.address
      });
      setEditMode(false);
      setSuccess('Company profile updated successfully');
      
      if (onUpdate) {
        onUpdate();
      }
    } catch (err) {
      console.error('Error updating profile:', err);
      setError('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditMode(false);
    setEditData({
      companyName: companyData?.companyName || '',
      industry: companyData?.industry || '',
      department: companyData?.department || '',
      firstName: companyData?.firstName || '',
      lastName: companyData?.lastName || '',
      title: companyData?.title || '',
      email: companyData?.email || '',
      phone: (companyData as any)?.phoneNumber || companyData?.phone || '',
      address: (companyData as any)?.streetAddress1 || companyData?.address || '',
      website: companyData?.website || '',
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={compact ? 200 : 400}>
        <CircularProgress />
      </Box>
    );
  }

  const logoSize = compact ? 88 : 120;
  const isAdmin = userProfile?.role === 'Admin';
  const showCameraButton = isAdmin && !companyData?.logo;
  const logoClickable = isAdmin && !!companyData?.logo;

  const logoSection = (
    <LogoContainer>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        hidden
        onChange={handleLogoUpload}
      />
      <Box
        position="relative"
        display="inline-block"
        onClick={logoClickable ? () => fileInputRef.current?.click() : undefined}
        sx={
          compact
            ? {
                width: logoSize,
                height: logoSize,
                minWidth: logoSize,
                minHeight: logoSize,
                ...(logoClickable && { cursor: 'pointer', borderRadius: 1, '&:hover': { opacity: 0.9 } }),
              }
            : undefined
        }
      >
        {companyData?.logo ? (
          <LogoWithFallback
            src={companyData.logo}
            alt={companyData.companyName}
            companyName={companyData.companyName}
            onError={() => console.log('Logo failed to load:', companyData.logo)}
            size={logoSize}
          />
        ) : (
          <CompanyLogo sx={compact ? { width: logoSize, height: logoSize } : undefined}>
            <BusinessIcon sx={{ fontSize: compact ? '2rem' : '3rem' }} />
          </CompanyLogo>
        )}
        {showCameraButton && (
          <UploadButton
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            size="small"
            sx={compact ? { bottom: -4, right: -4 } : undefined}
          >
            {uploading ? (
              <CircularProgress size={20} />
            ) : (
              <PhotoCameraIcon />
            )}
          </UploadButton>
        )}
      </Box>
      {!companyData?.logo && !compact && (
        <Typography variant="body2" color="text.secondary" mt={1}>
          No logo uploaded yet. Click the camera button to upload one.
        </Typography>
      )}
    </LogoContainer>
  );

  const profileContent = compact ? (
    <Box display="flex" flexDirection="row" alignItems="center" gap={3} flexWrap="wrap" justifyContent="flex-end">
      {logoSection}
      <Box display="flex" flexDirection="column" alignItems="flex-end" gap={0.5}>
        <Typography variant="h6" fontWeight="bold" color="text.primary">
          {companyData?.companyName}
        </Typography>
        {(companyData?.industry || companyData?.department) && (
          <Typography variant="body2" color="text.secondary">
            {[companyData?.industry, companyData?.department].filter(Boolean).join(' · ')}
          </Typography>
        )}
      </Box>
      <Box display="flex" alignItems="center" gap={2}>
        <Box display="flex" alignItems="center" gap={0.5}>
          <PeopleIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
          <Typography variant="body2" color="text.primary">
            {userCount} {userCount === 1 ? 'User' : 'Users'}
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={0.5}>
          <CheckCircleIcon sx={{ color: 'success.main', fontSize: 18 }} />
          <Typography variant="body2" color="text.primary">
            Active
          </Typography>
        </Box>
      </Box>
    </Box>
  ) : (
    <Box display="flex" flexDirection="column" alignItems="center">
      {logoSection}
      <Typography variant="h4" fontWeight="bold" color="text.primary" gutterBottom>
        {companyData?.companyName}
      </Typography>
      {companyData?.industry && (
        <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
          {companyData.industry}
        </Typography>
      )}
      {companyData?.department && (
        <Typography variant="body1" color="text.secondary">
          {companyData.department}
        </Typography>
      )}
      <Box display="flex" gap={3} mt={2} flexWrap="wrap">
        <Box display="flex" alignItems="center" gap={1}>
          <PeopleIcon sx={{ color: 'text.secondary' }} />
          <Typography color="text.primary">
            {userCount} {userCount === 1 ? 'User' : 'Users'}
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={1}>
          <CheckCircleIcon sx={{ color: 'success.main' }} />
          <Typography color="text.primary">
            Active
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  return (
    <>
      {compact ? (
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-end',
            alignItems: 'center',
            '& .MuiAvatar-root': { width: 88, height: 88 },
            '& img': { maxWidth: 88, maxHeight: 88, objectFit: 'contain' },
          }}
        >
          {profileContent}
        </Box>
      ) : (
        <ProfileCard>
          <HeaderSection>
            {profileContent}
          </HeaderSection>
        </ProfileCard>
      )}

      {/* Edit Profile Dialog */}
      <Dialog
        open={editMode}
        onClose={handleCancel}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            minHeight: '500px'
          }
        }}
      >
        <DialogTitle>
          <Typography variant="h5" fontWeight="bold" color="text.primary">
            Edit Company Profile
          </Typography>
        </DialogTitle>
        <DialogContent>
          <InfoGrid sx={{ mt: 2 }}>
            <StyledTextField
              label="Company Name"
              value={editData.companyName}
              onChange={(e) => setEditData({ ...editData, companyName: e.target.value })}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <BusinessIcon />
                  </InputAdornment>
                ),
              }}
            />
            <StyledTextField
              label="Industry"
              value={editData.industry}
              onChange={(e) => setEditData({ ...editData, industry: e.target.value })}
              fullWidth
              placeholder="e.g., Finance, Retail, Healthcare"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CategoryIcon />
                  </InputAdornment>
                ),
              }}
            />
            <StyledTextField
              label="Department"
              value={editData.department}
              onChange={(e) => setEditData({ ...editData, department: e.target.value })}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <WorkIcon />
                  </InputAdornment>
                ),
              }}
            />
            <StyledTextField
              label="First Name"
              value={editData.firstName}
              onChange={(e) => setEditData({ ...editData, firstName: e.target.value })}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon />
                  </InputAdornment>
                ),
              }}
            />
            <StyledTextField
              label="Last Name"
              value={editData.lastName}
              onChange={(e) => setEditData({ ...editData, lastName: e.target.value })}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon />
                  </InputAdornment>
                ),
              }}
            />
            <StyledTextField
              label="Title"
              value={editData.title}
              onChange={(e) => setEditData({ ...editData, title: e.target.value })}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <BadgeIcon />
                  </InputAdornment>
                ),
              }}
            />
            <StyledTextField
              label="Email"
              value={editData.email}
              onChange={(e) => setEditData({ ...editData, email: e.target.value })}
              fullWidth
              type="email"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon />
                  </InputAdornment>
                ),
              }}
            />
            <StyledTextField
              label="Phone"
              value={editData.phone}
              onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PhoneIcon />
                  </InputAdornment>
                ),
              }}
            />
            <StyledTextField
              label="Website"
              value={editData.website}
              onChange={(e) => setEditData({ ...editData, website: e.target.value })}
              fullWidth
              placeholder="https://www.example.com"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LanguageIcon />
                  </InputAdornment>
                ),
              }}
            />
            <StyledTextField
              label="Address"
              value={editData.address}
              onChange={(e) => setEditData({ ...editData, address: e.target.value })}
              fullWidth
              multiline
              rows={2}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LocationIcon />
                  </InputAdornment>
                ),
              }}
            />
          </InfoGrid>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<CancelIcon />}
            onClick={handleCancel}
            disabled={saving}
            size="large"
          >
            Cancel
          </Button>
          <EditButton
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving}
            size="large"
          >
            {saving ? <CircularProgress size={20} /> : 'Save Changes'}
          </EditButton>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!success}
        autoHideDuration={4000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      </Snackbar>

      <ImageCropModal
        open={cropModalOpen}
        onClose={() => {
          setCropModalOpen(false);
          setSelectedFile(null);
        }}
        imageFile={selectedFile}
        onCropComplete={handleCroppedImageUpload}
        aspectRatio={1}
      />

    </>
  );
};

export default CompanyProfile;
